/**
 * @fileoverview ExperienceCard.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React from 'react'
import DynamicImage from '../ui/DynamicImage'
import { motion } from 'framer-motion'

function ExperienceCard({ experience, index = 0, onClick, variant = 'default' }) {
  if (variant === 'overlay') {
    return (
      <motion.div
        onClick={() => onClick && onClick(experience)}
        className='relative w-full aspect-square rounded-3xl overflow-hidden group cursor-pointer shadow-sm hover:shadow-xl transition-all duration-300'
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.15 }}
        variants={{
          hidden: { opacity: 0, scale: 0.95 },
          visible: {
            opacity: 1,
            scale: 1,
            transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: index * 0.05 },
          },
        }}
      >
        <DynamicImage 
          LowImage={experience.image.l} 
          HighImage={experience.image.h} 
          alt={`Experience: ${experience.name} in Sri Lanka`} 
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none transition-opacity duration-300"></div>
        <div className="absolute bottom-0 left-0 w-full p-5 flex flex-col justify-end">
          <h3 className='text-lg md:text-xl font-bold text-white leading-snug drop-shadow-md group-hover:text-KITH-primary transition-colors'>
            {experience.name}
          </h3>
        </div>
      </motion.div>
    )
  }

  // Default Grid Layout (Image top, text bottom)
  return (
    <motion.div
      onClick={() => onClick && onClick(experience)}
      className='flex flex-col w-full bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 border border-gray-100 cursor-pointer group'
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
      variants={{
        hidden: { opacity: 0, y: 40 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: index * 0.08 },
        },
      }}
    >
      {/* Image Section */}
      <div className='w-full aspect-[4/3] relative overflow-hidden flex-shrink-0'>
        <DynamicImage 
          LowImage={experience.image.l} 
          HighImage={experience.image.h} 
          alt={`Experience: ${experience.name} in Sri Lanka`} 
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
      </div>

      {/* Content Section */}
      <div className='w-full p-6 flex flex-col gap-3 text-left flex-grow'>
        <h3 className='text-lg md:text-xl font-bold text-gray-800 leading-tight group-hover:text-KITH-primary transition-colors'>
          {experience.name}
        </h3>

        <div className='flex flex-wrap gap-1.5 mt-auto pt-2'>
          {experience.categories.slice(0, 3).map((cat, idx) => (
            <span
              key={idx}
              className='px-2.5 py-1 text-[9px] md:text-[10px] font-bold text-KITH-primary border border-KITH-primary/30 bg-KITH-primary/5 rounded uppercase tracking-wider'
            >
              {cat}
            </span>
          ))}
          {experience.categories.length > 3 && (
            <span className='px-2.5 py-1 text-[9px] md:text-[10px] font-bold text-gray-500 border border-gray-200 bg-gray-50 rounded uppercase tracking-wider'>
              +{experience.categories.length - 3}
            </span>
          )}
        </div>
        
        <div className="mt-2 text-xs md:text-sm font-bold text-gray-400 group-hover:text-KITH-primary transition-colors flex items-center gap-1.5">
          View Details <i className="fi fi-br-arrow-right text-[10px] mt-0.5 group-hover:translate-x-1 transition-transform"></i>
        </div>
      </div>
    </motion.div>
  )
}

export default ExperienceCard
